import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  wizardDistributionPartnerBlacklistApi,
  wizardDistributionPartnerBlacklistKeys,
} from '@client/api/waterlily';

export function useWizardDistributionPartnerBlacklist() {
  const queryClient = useQueryClient();

  const listQuery = useQuery({
    queryKey: wizardDistributionPartnerBlacklistKeys.list(),
    queryFn: () => wizardDistributionPartnerBlacklistApi.list(),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: wizardDistributionPartnerBlacklistKeys.list() });

  const addMutation = useMutation({
    mutationFn: ({ email, note }: { email: string; note: string | null }) =>
      wizardDistributionPartnerBlacklistApi.add(email, note),
    onSuccess: () => {
      toast.success('Added to deny list');
      invalidate();
    },
    onError: (error: unknown) => {
      toast.error('Failed to add to deny list', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => wizardDistributionPartnerBlacklistApi.remove(id),
    onSuccess: () => {
      toast.success('Removed from deny list');
      invalidate();
    },
    onError: (error: unknown) => {
      toast.error('Failed to remove from deny list', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    },
  });

  return {
    entries: listQuery.data ?? [],
    isLoading: listQuery.isLoading,
    addEntry: addMutation.mutate,
    isAdding: addMutation.isPending,
    removeEntry: removeMutation.mutate,
    isRemoving: removeMutation.isPending,
  };
}
